<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import _ from '@/setup/lodash';
import { useApi } from '@/api';
import { useError } from '@/composables/useError';
import { useFormatters } from '@/composables/useFormatters';
import type {
  GetAffiliatesData,
  GetAffiliatesResponse,
  GetMediaBuyingCampaignsTransferableBalanceRecipientsResponse,
  GetMediaBuyingCampaignsTransferableBalancesResponse,
  PostMediaBuyingPaymentsTransfersData,
} from '@/types/openapi-ts';
import type { EmitsToListeners, ToErrorMap } from '@/types/core';
//#endregion

//#region types
type FormErrors = ToErrorMap<PostMediaBuyingPaymentsTransfersData['body']>;
type GetAffiliatesQuery = NonNullable<GetAffiliatesData['query']>;

type FormQuery = Omit<GetAffiliatesQuery, 'account_status'> & {
  account_status?: GetAffiliatesQuery['account_status'] | Array<GetAffiliatesQuery['account_status']>;
};

export interface MediaBuyingTransferParams {
  data?: any;
  listeners?: EmitsToListeners<Emits>;
}

interface Emits  {
  success: [];
  close: [],
}
//#endregion

//#region define
const emit = defineEmits<Emits>();
//#endregion

//#region const
const debouncedFetchers = {
  from: _.debounce(getCampaignOptionsFrom, 800),
  to: _.debounce(getCampaignOptionsTo, 800),
};
//#endregion

//#region composables
const api = useApi();
const { errors, getError } = useError<NonNullable<FormErrors>>();
const { formatCurrencyValue } = useFormatters();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);

const selectLoading = ref(false);

const affiliateOptions = ref<GetAffiliatesResponse['payload']>([]);
const campaignOptionsFrom = ref<GetMediaBuyingCampaignsTransferableBalancesResponse['payload']>([]);
const campaignOptionsTo = ref<GetMediaBuyingCampaignsTransferableBalanceRecipientsResponse['payload']>([]);
const balances = ref<any[]>([]);

const fromAffiliate = ref('');
const toAffiliate = ref('');

const fromCampaign = ref('');
const toCampaign = ref('');
const balance = ref('');
const amount = ref(0);
//#endregion

//#region computed
const formattedBalances = computed(() => {
  return balances.value.map(((el, i) => ({
    ...el,
    id: `${el.currency_code}:${el.amount}:${i}`,
    label: formatCurrencyValue(el.currency_code, el.amount),
  })));
});
//#endregion

//#region methods
async function open() {
  isVisible.value = true;
}

function handleSave() {
  isLoading.value = true;

  const promise = api.postMediaBuyingPaymentsTransfers({
    from_campaign_id: fromCampaign.value,
    to_campaign_id: toCampaign.value,
    currency_code: balance.value.split(':')?.[0]!,
    amount: amount.value,
  });

  promise.then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    errors.value = e?.data?.errors || {};
  }).finally(() => isLoading.value = false);
}

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};

function searchMethod(v: string) {
  selectLoading.value = true;
  _.debounce(getAffiliates, 800)(v);
}

function searchCampaignMethod(search: string, type: 'from' | 'to') {
  selectLoading.value = true;
  debouncedFetchers[type](search);
}

function getAffiliates(search: string) {
  const query: FormQuery = {
    limit: 100,
    offset: 0,
    search,
    account_status: ['approved'],
    media_buying_enabled: true,
  };

  return new Promise((resolve, reject) => {
    api
      .getAffiliates(query as GetAffiliatesData['query'])
      .then((response) => {
        affiliateOptions.value = response.data.payload;
        resolve(response.data.payload);
      })
      .catch((error) => {
        reject(error);
      })
      .finally(() => {
        selectLoading.value = false;
      });
  });
}

function getCampaignOptionsFrom(search: string) {
  const query = {
    search,
    affiliate_id: fromAffiliate.value,
  };

  return new Promise((resolve, reject) => {
    api
      .getMediaBuyingPaymentsTransferableBalances(query)
      .then((response) => {
        campaignOptionsFrom.value = response.data.payload
          .filter((el) => !!el.balances.length)
          .map((el) => ({
            ...el,
            label: `(ID: ${el.incrementing_id}) ${el.name}`,
          }));
        resolve(response.data.payload);
      })
      .catch((error) => {
        reject(error);
      })
      .finally(() => {
        selectLoading.value = false;
      });
  });
}

function getCampaignOptionsTo(search: string) {
  const query = {
    search,
    affiliate_id: toAffiliate.value,
  };

  return new Promise((resolve, reject) => {
    api
      .getMediaBuyingPaymentsTransferableBalancesRecipients(query)
      .then((response) => {
        campaignOptionsTo.value = (response.data.payload || []).map((el) => ({
          ...el,
          label: `(ID: ${el.incrementing_id}) ${el.name}`,
        }));
        resolve(response.data.payload);
      })
      .catch((error) => {
        reject(error);
      })
      .finally(() => {
        selectLoading.value = false;
      });
  });
}

function handlePickBalance(id: string) {
  balances.value = campaignOptionsFrom.value.find((item) => item.id === id)!.balances;
}

function preventSymbol(e: KeyboardEvent) {
  if (['e', 'E', '+', '-'].includes(e.key)) {
    e.preventDefault();
  }
}

function checkMax() {
  const [, maxAmount] = balance.value.split(':');

  amount.value = Math.min(+amount.value || 0, +maxAmount!);
}

function handleClickMax() {
  const [, maxAmount] = balance.value.split(':');
  amount.value = +maxAmount!;
}
//#endregion

//#region watch
watch(fromAffiliate, () => {
  fromCampaign.value = '';
});

watch(fromCampaign, () => {
  balance.value = '';
});

watch(balance, () => {
  amount.value = 0;
});

watch(toAffiliate, () => {
  toCampaign.value = '';
});
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="media-buying-transfer-popup"
    :title="$t('mediaBuying.transfer.add_transfer')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiSelectElement
          v-model="fromAffiliate"
          :label="$t('mediaBuying.transfer.from_affiliate')"
          :placeholder="$t('affiliates.transfer_popup.placeholder_affiliate')"
          :list="affiliateOptions"
          :loading="selectLoading"
          :remote-method="searchMethod"
          size="big"
          remote
          option-name-param="email"
          option-label-param="email"
          filterable
          :error="getError('affiliate_id_from')"
        />
        <UiSelectElement
          v-model="fromCampaign"
          :label="$t('mediaBuying.transfer.campaign')"
          :placeholder="$t('mediaBuying.transfer.campaign')"
          :list="campaignOptionsFrom"
          :loading="selectLoading"
          :remote-method="(search: string) => searchCampaignMethod(search, 'from')"
          :disabled="!fromAffiliate"
          size="big"
          remote
          option-name-param="label"
          option-label-param="label"
          filterable
          :error="errors.from_campaign_id"
          @change="handlePickBalance"
        />
        <UiSelectElement
          v-model="balance"
          :label="$t('mediaBuying.transfer.remaining_funds')"
          :placeholder="$t('mediaBuying.transfer.remaining_funds')"
          :list="formattedBalances"
          :disabled="!fromAffiliate || !fromCampaign"
          size="big"
          option-name-param="label"
          option-label-param="label"
          :error="errors.currency_code"
        />
        <UiInput
          v-model="amount"
          :label="$t('mediaBuying.transfer.amount')"
          :placeholder="$t('mediaBuying.transfer.amount')"
          :disabled="!balance"
          class="media-buying-transfer-popup__input"
          post-text="Max"
          autosize
          :error="getError('amount')"
          size="big"
          @blur="checkMax"
          @click-post="handleClickMax"
          @change="preventSymbol"
        />
        <UiSeparator />
        <UiSelectElement
          v-model="toAffiliate"
          :label="$t('mediaBuying.transfer.to_affiliate')"
          :placeholder="$t('affiliates.transfer_popup.placeholder_affiliate')"
          :list="affiliateOptions"
          :loading="selectLoading"
          :remote-method="searchMethod"
          size="big"
          remote
          option-name-param="email"
          option-label-param="email"
          filterable
          :error="getError('affiliate_id_to')"
        />
        <UiSelectElement
          v-model="toCampaign"
          :label="$t('mediaBuying.transfer.campaign')"
          :placeholder="$t('mediaBuying.transfer.campaign')"
          :list="campaignOptionsTo"
          :loading="selectLoading"
          :remote-method="(search: string) => searchCampaignMethod(search, 'to')"
          :disabled="!toAffiliate"
          :exclude-options="[fromCampaign]"
          size="big"
          remote
          option-name-param="label"
          option-label-param="label"
          filterable
          :error="errors.to_campaign_id"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isLoading"
          @click="handleSave"
        >
          {{ $t('mediaBuying.transfer.add_transfer') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.media-buying-transfer-popup {
  &__input .ui-input__append-content{
    color: var(--text-positive);
    font-size: 12px;
    cursor: pointer;
  }
}
</style>
